import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnInit,
	ViewChild,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { combineLatest } from "rxjs";
import { filter as rxFilter, take } from "rxjs/operators";
import { OfflineRatingService } from "src/app/cbta-web/services/offline-rating-calculation.service";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	CandidateSummary,
	DraftItem,
	FinalRatingsData,
	Maneuvers,
	RatingCandidateForm,
	StartRatingForm,
	StartRatingResp,
	StartRatingRespData,
	TS1Form,
	TS1FormResponseJSON,
} from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import * as uuid from "uuid";
import { CustomValidationService } from "../../../services/custom-validation.service";
import { ContainerRatingsService } from "../services/base/container-ratings.services";
import {
	createCandidateGroup,
	createCandidateRatingForm,
	createCandidateSignoffForm,
	createRatingFormContainer,
	createSessionGroup,
	createSignOffFormContainer,
	createTrainingInfoForm,
} from "../shared/base/form.factories";
import { TsRatingFormBase } from "../shared/base/ts-rating-form.base";
import {
	buildCategoriesFromData,
	buildSectionsFromTemplate,
	coerceBool,
	parseObLabel,
	parseObsCode,
} from "../shared/utils/observation.utils";

@Component({
	selector: "app-ts6-rating-form",
	templateUrl: "./ts6-rating-form.component.html",
	styleUrls: ["./ts6-rating-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts6RatingFormComponent extends TsRatingFormBase implements OnInit {
	// Optional inputs from parent containers.
	@Input() data?: { data: any };
	@Input() sessionDetails?: any;
	@Input() observationDetails?: any;

	// View references.
	@ViewChild("baseContainer") baseContainer?: ElementRef;
	@ViewChild("stepper") override stepper!: MatStepper;

	// Root forms.
	ts6TrainingInfoForm!: UntypedFormGroup;
	ts6RatingForm!: UntypedFormGroup;
	ts6SignOffForm!: UntypedFormGroup;

	// Component-specific collections.
	finalratingArray: FinalRatingsData[] = [];
	candidateSummary: CandidateSummary[] = [];
	override assessmentConfig: AssessmentConfigRes[] = [];
	TS6Form!: TS1Form;

	inPreviewSummary: boolean = false;
	seenSummary: boolean = false;
	previewCandidates: any[] = [];

	constructor(
		protected override fb: UntypedFormBuilder,
		private httpService: HttpService,
		public cd: ChangeDetectorRef,
		protected override loadingService: LoaderService,
		public sharedService: SharedService,
		public router: Router,
		protected override route: ActivatedRoute,
		protected override roleService: RoleService,
		private cvs: CustomValidationService,
		protected override profileService: ProfileService,
		protected override dialog: MatDialog,
		protected override containerRatingsService: ContainerRatingsService,
		protected offlineRatingService: OfflineRatingService,
	) {
		super();
		this.initializeComponent();
	}

	togglePreviewSummary() {
		this.inPreviewSummary = !this.inPreviewSummary;
		if (this.inPreviewSummary) {
			this.previewCandidates = this.getPreviewSummaryData();
			try {
				sessionStorage.setItem("quick_summary", JSON.stringify(this.previewCandidates));
			} catch {}
		}
	}

	onPreviewFinished(done: boolean) {
		this.seenSummary = done;
		this.togglePreviewSummary();
		this.initialize();
		this.scrollToTop();
	}

	// Map abstract getters to concrete root forms.
	protected override get trainingInfoForm(): UntypedFormGroup {
		return this.ts6TrainingInfoForm;
	}
	protected override get ratingRootForm(): UntypedFormGroup {
		return this.ts6RatingForm;
	}
	protected override get signOffRootForm(): UntypedFormGroup {
		return this.ts6SignOffForm;
	}

	// Convenience for layout.
	get baseContainerWidth(): number {
		return this.baseContainer?.nativeElement.offsetWidth ?? 0;
	}

	// Connectivity flags used by the UI.
	get isOnline(): boolean {
		return navigator.onLine;
	}
	get isSynced(): boolean {
		return this.containerRatingsService.isSynced;
	}

	// Frequently accessed controls.
	get formType() {
		return this.ts6TrainingInfoForm.get("formType")!;
	}
	get formName() {
		return this.ts6TrainingInfoForm.get("formName")!;
	}
	get syllabusStartDate() {
		return this.ts6TrainingInfoForm.get("syllabusStartDate")!;
	}
	get syllabusEndDate() {
		return this.ts6TrainingInfoForm.get("syllabusEndDate")!;
	}
	get flightSimulation() {
		return this.ts6TrainingInfoForm.controls["flightSimulation"] as UntypedFormGroup;
	}
	get candidatesArray() {
		return this.candidates;
	}
	get ratingFormArray() {
		return this.ratingForm;
	}
	get signOffFormArray() {
		return this.signOffForm;
	}
	get airRoute() {
		return this.flightSimulation.get("airRoute");
	}

	ngOnInit(): void {
		this.initialize();
	}

	initialize() {
		combineLatest([
			this.containerRatingsService.getFormData(),
			this.containerRatingsService.getObservationData(),
			this.containerRatingsService.getSessionData(),
		])
			.pipe(
				rxFilter(
					([formData, observationDetails, sessionDetails]) =>
						!!formData && Array.isArray(observationDetails) && observationDetails.length > 0 && !!sessionDetails,
				),
				take(1),
			)
			.subscribe(([formData, observationDetails, sessionDetails]) => {
				this.initializeAllForms();
				this.loadForm(formData as any, observationDetails as any, sessionDetails as any);
				this.attachFormListenersOnce();
			});
	}

	/**
	 * Create the three step root forms.
	 */
	private initializeAllForms(): void {
		this.ts6TrainingInfoForm = createTrainingInfoForm(this.fb);
		this.ts6RatingForm = createRatingFormContainer(this.fb);
		this.ts6SignOffForm = createSignOffFormContainer(this.fb);
	}

	/**
	 * Populate Step 1 fields, prepare candidate array, and set up permissions.
	 * If a draft was requested, load and apply it after options are ready.
	 */
	loadForm(
		resp: TS1FormResponseJSON,
		manuver: Array<{ name?: string; observation_category?: { code?: string } }> = [],
		session: any[] = [],
	): void {
		this.rawFormData = resp;

		this.candidates.clear();
		this.sessionKeys = session;

		const maneuvers: Maneuvers[] = manuver.length
			? [
					{
						maneuver: {
							code: resp.assessmentConfig?.[0]?.maneuverType?.code ?? "",
							description: resp.assessmentConfig?.[0]?.maneuverType?.description ?? "",
						},
						...buildCategoriesFromData(manuver),
					} as Maneuvers,
				]
			: [];

		this.observationData = maneuvers;
		this.sessionConfigs = session;
		this.assessmentConfig = [...resp.assessmentConfig];

		console.log("apakah draft", this.containerRatingsService?.draft);
		const date = this.containerRatingsService?.getInitialDate();

		this.formType.patchValue(resp.formType, { emitEvent: false });
		this.formName.patchValue(resp.name, { emitEvent: false });

		console.log("apakah data", date);
		console.log("apakah ini", resp.startDate);
		console.log("final", moment(new Date(date ?? resp.endDate)).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT));
		this.syllabusStartDate.patchValue(
			moment(new Date(date ?? resp.startDate)).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
			{ emitEvent: false },
		);

		this.syllabusEndDate.patchValue(
			moment(new Date(date ?? resp.endDate)).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
			{ emitEvent: false },
		);

		this.formSessionDetails = resp.sessionDetails.map((s) => s.code);

		console.log("this.formSessionDetails", this.formSessionDetails);
		const first = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_6);
		first.setControl("sessionDetails", createSessionGroup(this.fb, this.sessionKeys, this.formSessionDetails));
		this.candidates.push(first);

		if (!this.roleService.isAdmin) {
			this.formName.disable({ emitEvent: false });
			this.disableForm = true;
		}

		if (this.draftId) {
			this.loadDraftValues(this.draftId);
		}

		if (!this.isBootstrapping) {
			this.ts6TrainingInfoForm.updateValueAndValidity();
		}

		this.loadingService.setLoading(false);
	}

	/**
	 * Assemble the Step 1 payload to begin a rating session (online or offline).
	 */
	getStartRatingData(): StartRatingForm {
		const candidates: RatingCandidateForm[] = [];

		this.candidates.controls.map((c: any) => {
			const sessionDetailsRaw = c.get("sessionDetails")?.getRawValue();
			const grouped = this.buildSessionSelection(sessionDetailsRaw);

			candidates.push({
				name: c.value?.name,
				staffNumber: c.value?.staffNumber,
				licenseNumber: c.value?.licenseNo ?? "",
				licenseType: c.value?.licenseType ?? "",
				rank: c.value?.rank?.code,
				trainingProfile: c.value?.pilotProfile?.code,
				licenseExpiryDate: moment(c.value?.licenseExpiryDate).format("YYYY-MM-DD"),
				sessionDetails: [grouped],
				flightSimulatorDetails: {
					acType: "",
					accumulatedSectors: "",
					airportRoute: "",
					aircraftSimReg: "",
					sectorNo: "",
				},
			});
		});

		return {
			formId: this.formId,
			assessmentDate: moment(this.syllabusStartDate.value).format("YYYY-MM-DD"),
			candidates,
			instructorId: this.roleService.userDetails?.id ?? null,
		};
	}

	/**
	 * Build the trainingInformation structure, preserving candidate ids and primary flag.
	 */
	protected override getTrainingInformation(): StartRatingForm | undefined {
		const startRatingData = this.getStartRatingData();
		const existing: any[] = this.containerRatingsService?.draft?.trainingInformation?.candidates ?? [];

		// Build maps for robust lookup
		const byStaff: Map<string, any> = new Map();
		const byFingerprint: Map<string, any> = new Map(); // name+license fallback

		for (const e of existing) {
			if (e?.staffNumber) byStaff.set(String(e.staffNumber), e);
			const fp = `${(e?.name ?? "").trim().toLowerCase()}|${(e?.licenseNumber ?? "").trim().toLowerCase()}`;
			if (fp.trim() !== "|") byFingerprint.set(fp, e);
		}

		const candidates = startRatingData.candidates.map((c, i) => {
			// try: 1) staffNumber, 2) hidden candidateId from form (if you store one),
			// 3) fingerprint (name+license), 4) only then consider positional, 5) else new uuid
			const staffKey = String(c.staffNumber ?? "");
			const fpKey = `${(c.name ?? "").trim().toLowerCase()}|${(c.licenseNumber ?? "").trim().toLowerCase()}`;

			let prev = (staffKey && byStaff.get(staffKey)) || undefined;

			// (Optional) if you store candidateId in the form group:
			// const formId = this.candidates.at(i)?.get('candidateId')?.value;
			// if (!prev && formId) prev = existing.find(e => e?.candidateId === formId);

			if (!prev && fpKey.trim() !== "|") {
				prev = byFingerprint.get(fpKey);
			}

			if (!prev && existing[i] && existing[i]?.staffNumber === c.staffNumber) {
				// positional only when it looks like the same person
				prev = existing[i];
			}

			return {
				...c,
				candidateId: prev?.candidateId ?? uuid.v4(),
				isPrimary: typeof prev?.isPrimary === "boolean" ? prev.isPrimary : i === 0,
			};
		});

		return {
			...startRatingData,
			candidates,
			// keep assessmentDate consistent with other places (string iso)
			assessmentDate: moment(this.syllabusStartDate.value).format("YYYY-MM-DD"),
		};
	}

	/**
	 * Offline response shape for building Step 2 without a server call.
	 */
	getStartRatingOfflineResponse(trainingInformation?: StartRatingForm): StartRatingResp {
		const id = this.assessmentOfflineId ?? uuid.v4();
		this.assessmentOfflineId = id;

		const offlineCandidates =
			trainingInformation?.candidates?.map((d, i) => {
				return {
					id: d.candidateId!,
					name: d.name,
					staffNumber: d.staffNumber,
					isPrimary: d.isPrimary ?? i === 0,
				};
			}) ?? [];

		return {
			data: {
				candidates: offlineCandidates,
				traineeAssessmentId: id,
				assessmentConfig: this.assessmentConfig?.map(({ maneuverType, observations, assessmentId }) => {
					return {
						assessmentId,
						maneuver: { ...maneuverType, id: maneuverType.id ?? "" },
						observations: observations?.map((ob) => ({ ...ob, observationId: ob.id })),
					};
				}),
			},
		};
	}

	/**
	 * Extract Step 3 sign-off fields into a draft-friendly structure.
	 */
	protected override collectSignOffDetailsFromForm() {
		const out: any = { candidateSignOffDetails: {} };
		const arr = this.signOffForm?.getRawValue?.() ?? [];
		for (const row of arr) {
			if (!row?.candidateId) continue;
			out.candidateSignOffDetails[row.candidateId] = {
				ratingA: row.ratingA,
				ratingB: row.ratingB,
				ratingC: row.ratingC,
				finalRating: row.finalRating,
				category: row.category ?? "",
				sessionCheck: row.sessionCheck,
				objectiveCheck: row.objectiveCheck,
				clearanceCheck: row.clearanceCheck,
				instructorLicenseNumber: row.instructorLicenseNumber ?? "",
				useInstructorLicense: !!row.useInstructorLicense,
				trainerName: row.trainerName ?? "",
				traineeName: row.traineeName ?? "",
				trainerSignature: row.trainerSignature ?? null,
				traineeSignature: row.traineeSignature ?? null,
			};
		}
		return out;
	}

	getInvalidControls(form: UntypedFormGroup): string[] {
		const invalid: string[] = [];
		const controls = form.controls;

		for (const name in controls) {
			if (controls[name] instanceof UntypedFormGroup) {
				// Recursively check nested groups
				invalid.push(...this.getInvalidControls(controls[name] as UntypedFormGroup));
			} else {
				if (controls[name].invalid) {
					invalid.push(name);
				}
			}
		}

		return invalid;
	}

	/**
	 * Step 1 → Step 2 transition.
	 * Validates Step 1, builds rating forms and observations, and persists progress.
	 */
	override async onStepperNext(_stepper: MatStepper): Promise<void> {
		this.gotoTop();

		if (!this.isBootstrapping) {
			this.ts6TrainingInfoForm.updateValueAndValidity();
			this.ts6TrainingInfoForm.markAllAsTouched();
		}

		if (!this.ts6TrainingInfoForm.valid) {
			const invalidFields = this.getInvalidControls(this.ts6TrainingInfoForm);
			console.log("Invalid fields:", invalidFields);
			return;
		}

		this.ts6RatingForm = createRatingFormContainer(this.fb);

		if (this.ratingForm.controls.length > 0 && !this.isStep1FormCreated) {
			this.current = 0;
			this.prev = -1;
		}

		this.loadingService.setLoading(true);
		this.continuePage = Math.max(this.continuePage, 2);

		try {
			const trainingInformation = this.getTrainingInformation();
			if (!trainingInformation) {
				this.loadingService.setLoading(false);
				return;
			}

			const offlineResp = this.getStartRatingOfflineResponse(trainingInformation);
			this.startRatingSuccessOffline(offlineResp);

			this.containerRatingsService.setClearComments();

			this.cd.detectChanges();

			if (!this.isBootstrapping) {
				this.saveToDrafts(this.continuePage, { trainingInformation }, false).catch((err) =>
					console.error("saveToDrafts failed (non-blocking):", err),
				);
			}
		} catch (err) {
			console.error("onStepperNext failed:", err);
			this.loadingService.setLoading(false);
		}
	}

	/**
	 * Populate Step 2 with candidate rating sub-forms and observation groups.
	 * Apply preselected observations and restore any draft values.
	 */
	startRatingSuccessOffline(resp: StartRatingResp): void {
		this.isStep1FormCreated = true;
		this.getCandidateList();

		this.candidateList.forEach((c: string) => {
			this.ratingForm.push(createCandidateRatingForm(this.fb, c, this.assessmentOfflineId));
		});
		this.cd.detectChanges();

		const manu = resp.data.assessmentConfig.map((m) => ({
			maneuver: m.maneuver.description,
			maneuverCode: m.maneuver.code,
			assessmentId: m.assessmentId,
			traineeAssessmentId: resp.data.traineeAssessmentId,
		}));

		const candidates = resp.data.candidates;
		this.candidateList.forEach((_, i) => {
			const cand = candidates[i];
			if (!cand) return;

			const observations = this.ratingForm.at(i).get("observations") as UntypedFormArray;
			manu.forEach((m) => {
				observations.push(this.addOBs(m.maneuver, m.maneuverCode, m.assessmentId, m.traineeAssessmentId, cand.id));
			});
		});

		this.buildSelectedOb(resp.data);
		this.initRatingValues();
		this.cd.detectChanges();

		this.current = 0;
		this.prev = -1;
		this.normalizeCandidateView();

		this.stepper.next();
		this.loadingService.setLoading(false);
	}

	/**
	 * Rehydrate Step 2 values from the draft:
	 * - Add/remove actions and attempt counts.
	 * - Other comments per maneuver.
	 * - Preselected observation list.
	 * - Repeat counter and safety outcome per assessment.
	 */
	async initRatingValues() {
		try {
			if (!this.assessmentOfflineId) return;
			const record = await this.containerRatingsService.getDraftById(this.assessmentOfflineId);
			if (!record) return;

			const candidateRating = record?.candidateRating;
			const candidates = record?.trainingInformation?.candidates ?? [];

			const addRemoveOb = candidateRating?.addRemoveOb ?? [];
			const otherComments = candidateRating?.otherComments ?? [];
			const selectedOBs = candidateRating?.selectedOBs ?? [];
			const temRate = candidateRating?.temRate ?? [];
			const resetManeuvers = candidateRating?.resetManeuvers ?? [];

			for (const candidate of candidates) {
				const formIndex = candidates.findIndex((c: any) => c.staffNumber === candidate.staffNumber);
				const observations = this.ratingForm.at(formIndex)?.get("observations") as UntypedFormArray;
				if (!observations) continue;

				for (const a of addRemoveOb) {
					const idx = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === a.maneuverCode && i.candidateId === a.candidateId,
					);
					if (idx < 0) continue;

					const obsGroup = observations.at(idx) as UntypedFormGroup;
					const { cat, num } = parseObsCode(a.observationCode);

					let finalNum = num;
					if (finalNum == null) {
						const maybe = (selectedOBs || []).find(
							(s: any) =>
								s.candidateId === a.candidateId && s.maneuverCode === a.maneuverCode && parseObLabel(s.ob).cat === cat,
						);
						if (maybe) finalNum = parseObLabel(maybe.ob).num;
					}

					if (!cat || finalNum == null) continue;

					const ctrl = this.getObControl(obsGroup, cat, finalNum);
					if (!ctrl) continue;

					let isRemoved;
					const prev = ctrl.value;

					if (a.isRemoved != null) {
						isRemoved = !a.isRemoved ? CONSTANT.OB_LAST_ACTION.ADDED : CONSTANT.OB_LAST_ACTION.REMOVED;
					} else if (a.isRemoved == null) {
						isRemoved = CONSTANT.OB_LAST_ACTION.NO_ACTION;
					}

					const newValue = {
						...prev,
						totalCount: a.totalAttempt,
						currentCount: a.activeAttempt,
						isDisabled: a.isDisabled ?? false,
						lastAction: isRemoved,
					};

					ctrl.patchValue(newValue);
				}

				for (const comms of otherComments) {
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === comms.maneuverCode && i.candidateId === comms.candidateId,
					);
					if (i >= 0) (observations.at(i) as UntypedFormGroup).get("otherComents")?.patchValue(comms.comment);
				}

				const filterSelectedObs: Record<string, any> = {};
				for (const selectedOB of selectedOBs) {
					const code = selectedOB.maneuverCode;
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === code && i.candidateId === selectedOB.candidateId,
					);
					if (i >= 0) {
						filterSelectedObs[i] = filterSelectedObs[i] || { obs: [] };
						filterSelectedObs[i].obs.push(selectedOB);
					}
				}
				for (const i in filterSelectedObs) {
					const numI = parseInt(i, 10);
					(observations.at(numI) as UntypedFormGroup).get("selectedOBs")?.patchValue(filterSelectedObs[i].obs);
				}

				const repeatCount: Record<string, number> = {};
				for (const r of resetManeuvers) {
					const rId = `${r.candidateId}.${r.maneuverCode}`;
					repeatCount[rId] = (repeatCount[rId] ?? 0) + 1;
				}
				for (const key in repeatCount) {
					const [candidateId, maneuverCode] = key.split(".");
					const value = repeatCount[key];
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === maneuverCode && i.candidateId === candidateId,
					);
					if (i >= 0) (observations.at(i) as UntypedFormGroup).get("repeat")?.patchValue(value);
				}

				for (const tr in temRate as any) {
					const cid = tr.split(".")[0];
					const aid = tr.split(".")[1];
					const val = (temRate as any)[tr];
					if (cid && aid) {
						const rate = val.rate;
						const i = (observations?.value || []).findIndex(
							(i: any) => i.assessmentId === aid && i.candidateId === cid,
						);
						if (i >= 0) (observations.at(i) as UntypedFormGroup).get("safetyOutcome")?.patchValue(rate);
					}
				}
			}
		} catch (error) {
			console.error(error);
		}
	}

	/**
	 * Prepopulate observation rows for each candidate and maneuver using the config.
	 */
	buildSelectedOb(data: StartRatingRespData): void {
		const configs = data.assessmentConfig || [];

		for (let i = 0; i < this.candidateList.length; i++) {
			const obsFA = this.ratingForm.at(i).get("observations") as UntypedFormArray;
			if (!obsFA) continue;

			for (const cfg of data.assessmentConfig ?? []) {
				const mvCode = cfg.maneuver.code;
				const idx = obsFA.controls.findIndex((fg) => (fg as UntypedFormGroup).get("maneuverCode")!.value === mvCode);
				if (idx === -1) continue;

				const row = obsFA.at(idx) as UntypedFormGroup;

				// normalize to a type that *has* attempt
				const selectedObs = (cfg.observations ?? []).map((x: any) => ({
					code: x?.code as string,
					attempt: Number(x?.attempt ?? 0),
				}));

				for (const ob of selectedObs) {
					const [catLower, numStr] = (ob.code || "").split("_");
					const cat = (catLower || "").toUpperCase();
					const key = `${cat} ${Number(numStr)}`;
					const ctrl = (row.get(cat) as UntypedFormGroup)?.get(key);
					if (!ctrl) continue;

					ctrl.patchValue(
						{
							totalCount: ob.attempt,
							currentCount: ob.attempt,
							preDefined: ob.attempt > 0,
							lastAction: ob.attempt > 0 ? CONSTANT.OB_LAST_ACTION.PRE_SELECTED : CONSTANT.OB_LAST_ACTION.NO_ACTION,
							fixedManeuverCode: mvCode,
							isDisabled: false,
						},
						{ emitEvent: false },
					);
				}
			}
		}

		this.ratingFormLoaded = true;
		this.ts6RatingForm?.markAllAsTouched();
		this.cd.detectChanges();
	}

	/**
	 * Create a new observation group for a candidate and maneuver.
	 */
	addOBs(
		maneuver: string,
		maneuverCode: string,
		assessmentId: string,
		traineeAssessmentId: string,
		candidateId: string,
	): UntypedFormGroup {
		const tmpl = this.observationData?.[0] ?? {};
		const categoryTemplate = Object.fromEntries(
			Object.entries(tmpl).filter(([k, v]) => typeof v === "object" && v && k !== "maneuver"),
		);

		const sections = buildSectionsFromTemplate(this.fb, categoryTemplate);

		return this.fb.group({
			maneuver: [maneuver, []],
			maneuverCode: [maneuverCode, [Validators.required]],
			assessmentId: [assessmentId, [Validators.required]],
			traineeAssessmentId: [traineeAssessmentId, [Validators.required]],
			candidateId: [candidateId, [Validators.required]],
			...sections,
			safetyOutcome: [-2, []],
			repeat: [0, []],
			otherComents: ["", []],
			selectedOBs: [[], []],
		});
	}

	/**
	 * Ensure sign-off subforms match the current candidate set.
	 * If the set changed, clear and rebuild to maintain alignment.
	 */
	private rebuildSignOffFormsIfNeeded(): void {
		if (!this.ts6SignOffForm) {
			this.ts6SignOffForm = createSignOffFormContainer(this.fb);
		}

		const candidates = (this.getTrainingInformation()?.candidates ?? []).sort((a, b) =>
			a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1,
		);

		const existingIds = (this.signOffForm?.controls ?? []).map((fg) => fg.get("candidateId")?.value).filter((v) => !!v);

		const candidateIds = candidates.map((c) => c.candidateId).filter((v) => !!v);

		const needRebuild =
			existingIds.length !== candidateIds.length || candidateIds.some((id) => !existingIds.includes(id));

		if (!needRebuild) return;

		while (this.signOffForm.length) this.signOffForm.removeAt(this.signOffForm.length - 1);

		const signOffDetails = this.getSignOffDetailsData();
		const candidateSignOffDetails = signOffDetails?.candidateSignOffDetails ?? {};
		const trainerName = this.roleService.userDetails?.name ?? "";

		this.getCandidateList();

		candidates.forEach((c, index) => {
			if (!c.candidateId || !this.assessmentOfflineId) return;

			const details = candidateSignOffDetails[c.candidateId];

			// Determine if using instructor license or examiner number
			const useInstructorLicense =
				details?.useInstructorLicense !== undefined ? coerceBool(details?.useInstructorLicense, true) : true;

			// Get license number from draft, or profile service as fallback
			let instructorLicenseNumber = details?.instructorLicenseNumber;
			if (
				!instructorLicenseNumber ||
				(typeof instructorLicenseNumber === "string" && instructorLicenseNumber.trim() === "")
			) {
				// Try to get from profile service based on useInstructorLicense flag
				if (useInstructorLicense) {
					instructorLicenseNumber = this.profileService?.profile?.pilot_license_number ?? "";
				} else {
					instructorLicenseNumber = this.profileService?.profile?.examiner_number ?? "";
				}
			}

			const signOffData = {
				ratingA: details?.ratingA ?? 0,
				ratingB: details?.ratingB ?? 0,
				ratingC: details?.ratingC ?? 0,
				finalRating: details?.finalRating ?? 0,
				category: details?.category ?? "",
				sessionCheck: details?.sessionCheck,
				objectiveCheck: details?.objectiveCheck,
				clearanceCheck: details?.clearanceCheck,
				useInstructorLicense,
				instructorLicenseNumber: instructorLicenseNumber ?? "",
				trainerName: details?.trainerName ?? trainerName,
				traineeName: details?.traineeName ?? this.candidateList[index],
				trainerSignature: details?.trainerSignature ?? null,
				traineeSignature: details?.traineeSignature ?? null,
			};

			const form = createCandidateSignoffForm("ets_6", this.fb, {
				candidateName: this.candidateList[index],
				candidateId: c.candidateId,
				traineeAssessmentId: this.assessmentOfflineId!,
				trainerName,
				signOffDetails: signOffData,
			});

			this.signOffForm.push(form, { emitEvent: false });
		});

		this.cd.detectChanges();
	}

	/**
	 * Step 2 → Step 3 transition. Optionally bypass validation while restoring drafts.
	 */
	override onStepperNextSignOff(_stepper: MatStepper, bypassValidation: boolean = false) {
		if (!bypassValidation) {
			if (this.ts6RatingForm.invalid) {
				this.ts6RatingForm.markAllAsTouched();
				return;
			}
			this.saveSignOffDraft();
		}

		this.loadingService.setLoading(true);
		this.rebuildSignOffFormsIfNeeded();

		this.isStep2FormCreated = true;
		this.current = 0;
		this.prev = -1;
		this.continuePage = Math.max(this.continuePage, 3);

		if (bypassValidation) this.forceSelectStep(2);
		else this.stepper.next();

		this.loadingService.setLoading(false);
	}

	/**
	 * Final submission for each candidate. On success for all, navigate to summary.
	 */
	// onStepperComplete(_stepper: MatStepper): void {
	//   if (!navigator.onLine) {
	//     this.containerRatingsService.blockSubmitIfOffline(true);
	//     return;
	//   }

	//   const signoff = this.signOffForm.value;
	//   const candidate = this.ratingForm.value;
	//   const traineeAssessmentId = candidate[0].observations[0].traineeAssessmentId;

	//   this.completedCandidateList = [];
	//   this.loadingService.setLoading(true);

	//   signoff?.forEach((_s: any, i: number) => {
	//     const candidateFormData = new FormData();
	//     candidateFormData.append("candidateId", candidate[i].observations[0].candidateId);
	//     candidateFormData.append("is_complete", signoff[i].sessionCheck);
	//     candidateFormData.append("is_clear", signoff[i].clearanceCheck);
	//     candidateFormData.append("is_obj_met", signoff[i].objectiveCheck);
	//     candidateFormData.append("trainee_name", signoff[i].traineeName);
	//     candidateFormData.append("instructor_name", signoff[i].trainerName);
	//     candidateFormData.append("instructor_signature", signoff[i].trainerSignature);
	//     candidateFormData.append("trainee_signature", signoff[i].traineeSignature);
	//     candidateFormData.append("instructor_license_number", signoff[i].instructorLicenseNumber);
	//     this.completedCandidateList.push(candidate[i].observations[0].candidateId);

	//     this.httpService
	//       .candidateCompleteRating(traineeAssessmentId, candidateFormData)
	//       .subscribe({
	//         next: this.onSuccessSignOff.bind(this),
	//         error: this.onErrorSignOff.bind(this),
	//       });
	//   });
	// }

	// Public wrappers for template navigation that override base class methods.

	async onStepperComplete(_stepper: MatStepper): Promise<void> {
		if (!navigator.onLine) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}

		const signoff = this.signOffForm.value as Array<{
			traineeName: string;
			trainerName: string;
			instructorLicenseNumber?: string;
			useInstructorLicense?: boolean;
			traineeSignature: File | Blob | string; // dataURL or File/Blob
			trainerSignature: File | Blob | string; // dataURL or File/Blob
			sessionCheck?: boolean;
			clearanceCheck?: boolean;
			objectiveCheck?: boolean;
		}>;

		const candidate = this.ratingForm.value as Array<{
			observations: Array<{ candidateId: string; traineeAssessmentId?: string }>;
		}>;

		// Reuse your existing source of truth (no new component property)
		const trainingAssessmentId = (candidate?.[0]?.observations?.[0]?.traineeAssessmentId as string) || "";

		if (!trainingAssessmentId || !signoff?.length || !candidate?.length) {
			return;
		}

		// Build trainees payload: each will be sent as <candidateId>.png by the service
		const trainees = signoff.map((s, i) => ({
			candidateId: candidate[i].observations[0].candidateId,
			signature: null,
		}));

		// Instructor fields (same for the whole request; take from first row)
		const instructor_name = signoff[0].trainerName || "";
		// Get instructor license number from form, with fallback to profile service if empty
		let instructor_license_number = signoff[0].instructorLicenseNumber;
		if (
			!instructor_license_number ||
			(typeof instructor_license_number === "string" && instructor_license_number.trim() === "")
		) {
			// Check if profile service has the data available (might have loaded after form was created)
			const useInstructorLicense = signoff[0].useInstructorLicense ?? true;
			if (useInstructorLicense) {
				instructor_license_number = this.profileService?.profile?.pilot_license_number ?? "";
			} else {
				instructor_license_number = this.profileService?.profile?.examiner_number ?? "";
			}
		}
		// Ensure it's always a string
		instructor_license_number = instructor_license_number != null ? String(instructor_license_number).trim() : "";
		const instructor_signature = signoff[0].trainerSignature;

		if (this.containerRatingsService.inDraftMode) {
			// If online, try to sync the draft first before checking synced flag
			if (navigator.onLine) {
				const draftId = this.containerRatingsService.draft?.id || trainingAssessmentId || "";
				if (draftId) {
					const record = await this.containerRatingsService.getDraftById(draftId);
					if (record && record.synced !== true) {
						// Force sync the draft
						try {
							await this.containerRatingsService.syncOneDraft(record);
							// Reload draft to get updated synced flag
							await this.containerRatingsService.loadDraftItem(draftId);
						} catch (err) {
							console.error("Failed to sync draft before submission:", err);
						}
					}
				}
			}

			// Check synced flag after potential sync
			let synced = this.containerRatingsService.isSynced;

			// Double-check the persisted record to avoid a stale flag
			const draftId = this.containerRatingsService.draft?.id || trainingAssessmentId || "";
			if (draftId) {
				const record = await this.containerRatingsService.getDraftById(draftId);
				if (typeof record?.synced === "boolean") {
					synced = record.synced;
				}
			}

			// Only block if actually offline or still not synced after attempt
			if (!synced) {
				if (!navigator.onLine) {
					this.containerRatingsService.blockSubmitIfOffline(true);
				} else {
					// Online but sync failed - show error
					this.containerRatingsService.blockSubmitIfOffline(true);
				}
				return;
			}
		}

		// Minimal guard: all signatures must exist
		if (!instructor_signature) {
			return;
		}

		this.loadingService.setLoading(true);

		this.httpService
			.completeRatingWithFiles({
				trainingAssessmentId,
				instructor_name,
				instructor_license_number,
				instructor_signature,
				trainees, // service will name each file "<candidateId>.png"
			})
			.subscribe({
				next: (_res) => {
					const completedCandidateIds = _res.data.map((info: any) => {
						return info.updatedCandidateId;
					});
					this.loadingService.setLoading(false);
					this.completedCandidateList = completedCandidateIds;
					this.onSuccessSignOff();
				},
				error: (_err) => {
					this.onErrorSignOff(_err);
					// handle error UI if you want (toast/snackbar)
				},
			});
	}

	override onPrev() {
		super.onPrev();
	}
	override onNext() {
		super.onNext();
	}
	override isLeftTransition(idx: number): boolean {
		return super.isLeftTransition(idx);
	}

	/**
	 * Return to dashboard route.
	 */
	goback() {
		this.router.navigate([RouteType.DASHBOARD]);
	}

	/**
	 * Patch Step 1 from draft values and ensure candidate rows match.
	 */
	protected override loadDraftTrainingInfo(draft: DraftItem) {
		const trainingInfo = draft.trainingInformation;
		const targetCount = trainingInfo.candidates.length;

		while ((this.candidates?.length ?? 0) < targetCount) {
			const cg = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_6);
			cg.setControl("sessionDetails", createSessionGroup(this.fb, this.sessionKeys, this.formSessionDetails));
			this.candidates.push(cg, { emitEvent: false });
		}

		const toNestedSessions = (cand: any) => {
			const selected = new Set<string>([
				...(cand?.sessionDetails?.[0]?.trainingSession ?? []),
				...(cand?.sessionDetails?.[0]?.assessmentType ?? cand?.sessionDetails?.[0]?.assesmentType ?? []),
			]);
			const nested: any = {};
			for (const section of this.sessionKeys ?? []) {
				const sectionCode = section?.name?.code;
				nested[sectionCode] = {};
				for (const d of section?.details ?? []) {
					const code = typeof d === "string" ? d : d.code;
					nested[sectionCode][code] = selected.has(code);
				}
			}
			return nested;
		};

		this.candidates.patchValue(
			trainingInfo.candidates.map((c: any) => {
				const rankOption = this.asOption(this.sharedService.dropDownOptionsRank, c?.rank);
				const trainingProfileOption = this.asOption(this.sharedService.dropDownOptionsPilotProfile, c?.trainingProfile);

				return {
					name: c.name,
					rank: rankOption,
					staffNumber: c.staffNumber,
					licenseNo: c.licenseNumber,
					licenseType: c.licenseType,
					pilotProfile: trainingProfileOption,
					licenseExpiryDate: c.licenseExpiryDate,
					sessionDetails: toNestedSessions(c),
					flightSimulatorDetails: {
						acType: "",
						accumulatedSectors: "",
						airportRoute: "",
						aircraftSimReg: "",
						sectorNo: "",
					},
				};
			}),
			{ emitEvent: false },
		);

		// 2) Immediately enforce disable/clear rules per row
		(this.candidates.controls as UntypedFormGroup[]).forEach((fg) => {
			const rankVal = fg.get("rank")?.value;
			// handle either string rank or {code: string}
			const code = typeof rankVal === "string" ? rankVal : rankVal?.code;

			const shouldDisable = code === CONSTANT.RANK.pur || code === CONSTANT.RANK.scc;

			const licenseNo = fg.get("licenseNo");
			const licenseType = fg.get("licenseType");
			const licenseExpiryDate = fg.get("licenseExpiryDate");

			if (shouldDisable) {
				// Clear first (safer for mat-autocomplete) then disable
				licenseNo?.setValue(null, { emitEvent: false });
				licenseNo?.disable({ emitEvent: false });

				licenseType?.setValue(null, { emitEvent: false });
				licenseType?.disable({ emitEvent: false });

				licenseExpiryDate?.setValue(null, { emitEvent: false });
				licenseExpiryDate?.disable({ emitEvent: false });
			} else {
				licenseNo?.enable({ emitEvent: false });
				licenseType?.enable({ emitEvent: false });
				licenseExpiryDate?.enable({ emitEvent: false });
			}
		});
	}

	/**
	 * Add a new candidate row and persist the change.
	 */
	addNewCandidate() {
		if (!this.canAddCandidate()) return;

		const group = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_6);
		group.setControl("sessionDetails", createSessionGroup(this.fb, this.sessionKeys, this.formSessionDetails));
		this.candidates.push(group, { emitEvent: false });
		this.cd.detectChanges();

		this.isStep2FormCreated = false;
		this.saveTrainingDraft();
	}

	/**
	 * Remove the candidate at the provided index and keep all steps in sync.
	 */
	async removeCandidate(idx: number) {
		this.containerRatingsService.silenceAutosave = true;

		try {
			await this.containerRatingsService.deleteCandidateForSelectedDraft(idx);
			this.candidates.removeAt(idx, { emitEvent: false });

			if (this.ratingForm?.length > idx) {
				this.ratingForm.removeAt(idx, { emitEvent: false });
			}
			if (this.signOffForm?.length > idx) {
				this.signOffForm.removeAt(idx, { emitEvent: false });
			}

			this.candidateList = this.getCandidateList();
			this.normalizeCandidateView();
			this.isStep2FormCreated = false;

			await this.saveTrainingDraft();

			this.ts6TrainingInfoForm.updateValueAndValidity({ emitEvent: false });
			this.ts6RatingForm.updateValueAndValidity({ emitEvent: false });
			this.ts6SignOffForm.updateValueAndValidity({ emitEvent: false });
			this.cd.detectChanges();
		} finally {
			this.containerRatingsService.silenceAutosave = false;
		}
	}
}
